import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentStatus } from '../types';
import { usePaymentFlow } from '../hooks/usePaymentFlow';
import { usePaymentStore } from '../state/usePaymentStore';
import { PaymentStatusView } from '../components/PaymentStatusView';

/**
 * ─── Écran : Retour de paiement + vérification ──────────────────────────────
 *
 * Cible unique des URLs de retour du provider (approve/cancel/decline).
 * - approved  → état VERIFYING + polling du statut réel (le callback provider
 *               est asynchrone : "approuvé" sur la page ne suffit pas)
 * - cancelled → écran "Paiement annulé" + Réessayer
 * - declined  → écran "Paiement échoué" + Réessayer
 * Gère aussi Expired (timeout de vérification) et Network Error.
 */
export function PaymentReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { attempt, resumeFromReturn, retry, verifyAgain, reset } = usePaymentFlow();
  const resumed = useRef(false);

  // URL ouverte à froid, sans tentative en cours → retour aux plans.
  // (Évalué une seule fois au montage : reset() en fin de flow ne doit pas
  // déclencher ce garde pendant qu'on navigue volontairement ailleurs.)
  const cameInCold = useRef(usePaymentStore.getState().attempt === null);

  useEffect(() => {
    if (cameInCold.current) {
      navigate('/plans', { replace: true });
      return;
    }
    if (resumed.current) return;
    resumed.current = true;
    resumeFromReturn(params);
  }, [params, resumeFromReturn, navigate]);

  if (!attempt) return null;

  const status = attempt.status;
  const actions = [
    ...(status === PaymentStatus.SUCCESS
      ? [
          { label: 'Voir mon abonnement', onClick: () => { reset(); navigate('/subscription'); } },
          {
            label: 'Retour au tableau de bord',
            onClick: () => { reset(); navigate('/dashboard'); },
            variant: 'secondary' as const,
          },
        ]
      : []),
    ...(status === PaymentStatus.FAILED ||
    status === PaymentStatus.CANCELLED ||
    status === PaymentStatus.NETWORK_ERROR
      ? [{ label: 'Réessayer le paiement', onClick: () => void retry() }]
      : []),
    ...(status === PaymentStatus.EXPIRED || status === PaymentStatus.NETWORK_ERROR
      ? [{ label: 'Re-vérifier le paiement', onClick: verifyAgain, variant: 'secondary' as const }]
      : []),
    ...(status !== PaymentStatus.SUCCESS &&
    status !== PaymentStatus.VERIFYING &&
    status !== PaymentStatus.LOADING
      ? [
          {
            label: 'Retour aux plans',
            onClick: () => { reset(); navigate('/plans'); },
            variant: 'secondary' as const,
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-4 py-8">
      <PaymentStatusView status={status} detail={attempt.error} actions={actions} />
    </div>
  );
}
